import {
  forEach,
  get,
  isArray,
  isEmpty,
  isObject,
  pick,
  reduce,
  some,
  values,
  zipObjectDeep,
} from 'lodash';
import { DynamicObjectType, FilterFromUrlResponseType } from 'types/commons';
import moment from 'moment';
import i18n from 'i18next';
import { notification } from 'antd';
import { KEY_SEARCH_MORE } from 'constants/filters';

const getValidDataOfObj = (obj: DynamicObjectType, isFilter?: boolean) => {
  const validData = reduce(
    obj,
    (result, value, key) => {
      if (Array.isArray(value) && isFilter) {
        return value.length > 0 ? { ...result, [key]: value } : result;
      } else if (Array.isArray(value)) {
        return { ...result, [key]: value };
      }
      if (typeof value === 'object' && !isEmpty(value)) {
        const formatChildValue = getValidDataOfObj(value);
        return !isEmpty(formatChildValue)
          ? { ...result, [key]: formatChildValue }
          : result;
      }

      if (value || value === false || value === 0 || value === null) {
        // eslint-disable-next-line
        result[key] = value;
        return { ...result, [key]: value };
      }

      if (value === '' && !isFilter) {
        // eslint-disable-next-line
        result[key] = '';
      }
      return result;
    },
    {},
  );
  return validData;
};

export const getValidData = (filter: DynamicObjectType, isFilter?: boolean) =>
  getValidDataOfObj(filter, isFilter);

export const getFilterFromSearchParams = (
  paramsObj,
): FilterFromUrlResponseType => {
  const parsed: DynamicObjectType = {};
  if (!Object.keys(paramsObj || {})?.length) return {};
  Object.keys(paramsObj).forEach((key) => {
    const v = paramsObj[key];
    if (!Number.isNaN(Number(v))) {
      parsed[key] = v.toString().length > 16 ? v : Number(v);
    } else {
      parsed[key] = v;
    }

    try {
      parsed[key] = !Number.isNaN(Number(v))
        ? (parsed[key] = v.toString().length > 16 ? v : Number(v))
        : JSON.parse(parsed[key]);
    } catch (error) {
      parsed[key] = parsed[key] as
        | string
        | number
        | boolean
        | DynamicObjectType;
    }
  });
  const filter = {
    ...pick(parsed, KEY_SEARCH_MORE),
    q: parsed.q,
    orderBy: parsed.orderBy,
    pageSize: parsed.pageSize,
    page: parsed.page,
    join: parsed.join,
    filters: parsed.filters,
  };
  delete parsed.customLayout;
  delete parsed.pageSize;
  delete parsed.page;
  delete parsed.orderBy;
  delete parsed.q;
  delete parsed.join;
  return filter;
};

function paramsToObject(entries) {
  const result = {};
  for (const [key, value] of entries) {
    // each 'entry' is a [key, value] tupple
    result[key] = value;
  }
  return result;
}

export const getSearchParams = (str) => {
  if (!str) return {};
  const urlSearchParams = new URLSearchParams(
    str.substring(str.indexOf('?'), str.length),
  );

  const params = paramsToObject(urlSearchParams.entries());
  return params;
};

export function isImage(url: string) {
  return (
    some(['jpg', 'jpeg', 'png', 'webp', 'svg', 'gif', 'heic'], (e) =>
      `${url?.toLowerCase()}`?.endsWith(e),
    ) || url?.toLowerCase()?.indexOf?.('data:image') === 0
  );
}

export const getImageUrl = (
  _image: string | { url: string },
  width: string | number = 100,
  height: string | number = 0,
) => {
  const image = typeof _image === 'object' ? _image?.url : _image;
  // eslint-disable-next-line
  if (!image)
    return 'data:image/png;base64,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';
  if (!isImage(image)) {
    return image && (image.indexOf('http') > -1 || image.indexOf('https') > -1)
      ? image
      : `${process.env?.REACT_APP_PHOTO_HOST}/${image}`;
  }

  let _width =
    typeof width === 'number'
      ? width
      : width?.replace?.(/[^0-9]/g, '') || 'auto';
  let _height =
    typeof height === 'number'
      ? height
      : height?.replace?.(/[^0-9]/g, '') || 'auto';
  // console.log(_width, width)
  if (
    typeof width === 'string' &&
    (width?.indexOf?.('vw') || width?.indexOf?.('vh'))
  ) {
    _width = 'auto';
  }
  if (
    typeof height === 'string' &&
    (height?.indexOf?.('vw') || height?.indexOf?.('vh'))
  ) {
    _height = 'auto';
  }
  const sizeStr =
    width === 'auto' && height === 'auto'
      ? ''
      : `/${_width === 'auto' ? 0 : _width}x${
          _height === 'auto' ? 0 : _height
        }`;

  return image && (image.indexOf('http') > -1 || image.indexOf('https') > -1)
    ? image
    : `${process.env?.REACT_APP_PHOTO_WITH_RESIZE}${sizeStr}/${image}`;
  // : getPreviewImageUrl({
  //     image,
  //   });
};

export const getExtraFilterPromotions = (code?: string | number) =>
  JSON.stringify({
    $and: code
      ? [
          {
            code: {
              $eq: code,
            },
          },
        ]
      : [
          {
            $or: [
              {
                $and: [
                  {
                    startDate: {
                      $lte: moment().startOf('date').toISOString(),
                    },
                  },
                  {
                    endDate: {
                      $gte: moment().endOf('date').toISOString(),
                    },
                  },
                ],
              },
              {
                $and: [
                  {
                    endDate: {
                      $isnull: true,
                    },
                  },
                ],
              },
            ],
          },
          {
            isEvoucher: {
              $eq: false,
            },
          },
          {
            isActive: {
              $eq: true,
            },
          },
          {
            isPublic: {
              $eq: true,
            },
          },
          {
            code: {
              $notnull: true,
            },
          },
        ],
  });

export const getDataDetail = (
  data,
  key = 'detail',
  titleKey = 'name',
  defaultValue = {},
) => {
  if (Array.isArray(get(data, key))) {
    if (
      get(data, key)?.find((item) => item?.langCode === i18n?.language)?.[
        titleKey
      ]
    ) {
      return get(data, key)?.find((item) => item?.langCode === i18n?.language);
    }
    return (
      get(data, key)?.find((item) => item?.[titleKey]) ||
      (get(data, key)?.[0]?.[titleKey]
        ? get(data, key)?.[0]
        : { [titleKey]: data?.slug?.replace(/-/gi, ' ') })
    );
  }

  return (
    get(data, key) || { [titleKey]: defaultValue || data?.slug || data?.id }
  );
};

export function plainToFlattenObject(object) {
  const result = {};

  function flatten(obj, prefix = '') {
    forEach(obj, (value, key) => {
      if (Array.isArray(value) && typeof value?.[0] === 'string') {
        result[`${prefix}${key}`] = value;
      } else if (isObject(value)) {
        flatten(value, `${prefix}${key}.`);
      } else {
        result[`${prefix}${key}`] = value;
      }
    });
  }

  flatten(object);

  return result;
}

export const getRecordData = (record, source) => {
  // const arrKeys = source ? replaceAll(replaceAll(source, '\\[', '.'), '\\]', '').split('.') : [];
  // let data = record;
  // arrKeys.forEach(key => {
  //   data = data ? data[key] : data;
  // });
  return get(record, source);
};
export const convertPlainToNestedObject = (data) => {
  const parseToObj = zipObjectDeep(Object.keys(data), values(data));
  return parseToObj;
};

export const getFilterFromUrl = (searchStr) => {
  const parsed = {} as DynamicObjectType;
  if (!searchStr || searchStr.trim() === '') return {};
  const searchObj = new URLSearchParams(searchStr);
  for (const pair of searchObj.entries()) {
    try {
      parsed[pair[0]] = JSON.parse(pair[1]);
    } catch (error) {
      parsed[pair[0]] = pair[1];
    }
  }
  // decodeURIComponent(searchStr)
  //   .trim()
  //   .substring(searchStr?.[0] === '?' ? 1 : 0)
  //   .split('&')
  //   .forEach((text) => {
  //     const keyValue = text.split('=');
  //     if (!Number.isNaN(Number(keyValue[1]))) {
  //       parsed[keyValue[0]] = Number(keyValue[1]);
  //     } else {
  //       parsed[keyValue[0]] = keyValue[1];
  //     }

  //     try {
  //       parsed[keyValue[0]] = JSON.parse(parsed[keyValue[0]]);
  //     } catch (error) {
  //       parsed[keyValue[0]] = parsed[keyValue[0]];
  //     }
  //   });
  const filter = {
    // q: parsed.q,
    orderBy: parsed.orderBy,
    limit: parsed.limit,
    offset: parsed.offset,
  } as DynamicObjectType;
  delete parsed.limit;
  delete parsed.offset;
  delete parsed.orderBy;
  // delete parsed.q;
  filter.filter = parsed;
  return filter;
};

export const getIdByUrl = (props, location) => {
  const idFromPath =
    location.pathname.match(
      `${props?.customRedirectPath || props.model}/(.*)/edit`,
    ) ||
    location.pathname.match(
      `${props?.customRedirectPath || props.model}/(.*)/show`,
    );
  const idFromHash =
    location.hash.match(`#${props.model}/(.*)/edit`) ||
    location.hash.match(`#${props.model}/(.*)`);
  return (idFromPath && idFromPath[1]) || (idFromHash && idFromHash[1]);
};

export const checkRole = (elementRoles, userRole, actionType, pass) => {
  return (
    pass ||
    !elementRoles ||
    !userRole ||
    !!userRole?.[elementRoles]?.find((action) =>
      Array.isArray(action)
        ? !!action?.find((e) => e === actionType)
        : action === actionType,
    )
  );
  // return !elementRoles || (userRole && elementRoles.indexOf(userRole) > -1);
};
export const generateCode = () =>
  (Math.random() + 1).toString(36).substring(6).toUpperCase();

export const generateUTMLink = (url = '', obj) => {
  const searchUrl = new URLSearchParams();
  Object.keys(obj).forEach((key) => {
    obj[key] && searchUrl.set(key, obj[key]);
  });
  return `${process.env.REACT_APP_CLIENT_URL}${url.replace(
    process.env.REACT_APP_CLIENT_URL,
    '',
  )}?${searchUrl.toString()}`;
};

export function copyUrl(data) {
  const Url = document.createElement('input');
  Url.id = 'copy-input';
  document.getElementById('root').appendChild(Url);
  Url.setAttribute('value', data);
  Url.select();
  Url.setSelectionRange(0, 99999);
  document.execCommand('copy');

  notification.success({
    message: 'Copied ' + data,
  });
  Url.remove();
}

export const getQueryParams = (searchStr) => {
  const parsed = {};

  if (!searchStr || searchStr.trim() === '') return {};

  const urlParams = new URLSearchParams(searchStr);

  const entries = urlParams.entries();

  // eslint-disable-next-line no-restricted-syntax
  for (const entry of entries) {
    try {
      parsed[entry[0]] = JSON.parse(entry[1]);
    } catch (error) {
      // eslint-disable-next-line prefer-destructuring
      parsed[entry[0]] = entry[1];
    }
  }

  return parsed;
};

export const convertObjToSearchStr = (params) =>
  Object.keys(params)
    .map((key) =>
      params[key]
        ? key === 'q'
          ? `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
          : `${encodeURIComponent(key)}=${encodeURIComponent(
              JSON.stringify(params[key]),
            )}`
        : '',
    )
    .filter((data) => data !== '')
    .join('&');

export const removeEmptyValueOfPathUrl = (path) => {
  const arr = path?.split('?');
  const searchArr = arr ? arr.slice(1, arr.length) : [];
  const params = searchArr ? getQueryParams(`?${searchArr.join('')}`) : {};

  const result = {};
  for (const key in params) {
    const value = params[key];
    if (
      value === false ||
      value === 0 ||
      (value && (typeof value === 'object' ? !isEmpty(value) : value))
    ) {
      result[key] = value;
    }
  }

  const searchStr = convertObjToSearchStr(result);

  return `${arr[0]}${searchStr ? `?${searchStr}` : ''}`;
};

export const getAvatarByName = (name) => {
  if (!name) return;
  const nameArray = name.toUpperCase().trim().split(' ');
  const initials = nameArray.map((word) => word[0]).join('');

  if (initials.length <= 2) {
    return initials;
  } else {
    return initials.substr(initials.length - 2);
  }
};

interface Permission {
  endpoint: string | string[];
  enable: string[];
}

interface Role {
  rolePermissions: Permission[];
}

export const newCheckRole = (
  endpointApi: string | string[],
  actionType: string,
  userRoles: Role[],
  pass: boolean,
): boolean => {
  if (pass) return true;

  return userRoles?.some((role) =>
    role?.rolePermissions?.some((permission) => {
      if (isArray(endpointApi)) {
        return endpointApi.some((api) => permission.endpoint === api);
      }
      return (
        permission.endpoint === endpointApi &&
        permission.enable.includes(actionType)
      );
    }),
  );
};
